import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Paper,
  Typography,
  Skeleton,
  Chip,
  Divider,
} from '@mui/material';
import { useChargebee } from 'client/modules/App/ChargebeeContext';
import { getProfile } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getToken } from '../../../Auth/AuthReducer';
import PricingPlans from '../../../Public/Pricing/pages/PricingPage/PricingPlans';
import { getPlans } from '../../../Public/Pricing/PricingReducer';

const messages = defineMessages({
  title: {
    id: 'Billing.Title',
    defaultMessage: 'Billing & Subscription',
  },
  subscription: {
    id: 'Billing.Subscription',
    defaultMessage: 'Subscription Management',
  },
  paymentMethods: {
    id: 'Billing.PaymentMethods',
    defaultMessage: 'Payment Methods',
  },
  billingHistory: {
    id: 'Billing.BillingHistory',
    defaultMessage: 'Billing History',
  },
  manageSubscription: {
    id: 'Billing.ManageSubscription',
    defaultMessage: 'Manage Subscription',
  },
  managePaymentMethods: {
    id: 'Billing.ManagePaymentMethods',
    defaultMessage: 'Manage Payment Methods',
  },
  viewBillingHistory: {
    id: 'Billing.ViewBillingHistory',
    defaultMessage: 'View Billing History',
  },
  customerInfo: {
    id: 'Billing.CustomerInfo',
    defaultMessage: 'Customer Information',
  },
  billingAddress: {
    id: 'Billing.BillingAddress',
    defaultMessage: 'Billing Address',
  },
  editPaymentMethod: {
    id: 'Billing.EditPaymentMethod',
    defaultMessage: 'Edit Payment Method',
  },
  editAccountDetails: {
    id: 'Billing.EditAccountDetails',
    defaultMessage: 'Edit account details',
  },
  editBillingAddress: {
    id: 'Billing.EditBillingAddress',
    defaultMessage: 'Edit billing address',
  },
  nextBilling: {
    id: 'Billing.NextBilling',
    defaultMessage: 'Next billing: {date}',
  },
  cardExpires: {
    id: 'Billing.CardExpires',
    defaultMessage: 'Expires {month}/{year}',
  },
  statusActive: {
    id: 'Billing.Status.Active',
    defaultMessage: 'Active',
  },
  statusTrial: {
    id: 'Billing.Status.Trial',
    defaultMessage: 'Trial',
  },
  statusCancelled: {
    id: 'Billing.Status.Cancelled',
    defaultMessage: 'Cancelled',
  },
  statusPastDue: {
    id: 'Billing.Status.PastDue',
    defaultMessage: 'Past Due',
  },
  manage: {
    id: 'Billing.Manage',
    defaultMessage: 'Manage',
  },
  billingHistoryDescription: {
    id: 'Billing.BillingHistoryDescription',
    defaultMessage: 'Download invoices and view your complete billing history.',
  },
  errorLoading: {
    id: 'Billing.Error',
    defaultMessage: 'Error loading billing information. Please try again.',
  },
});

const BillingPage = () => {
  const { formatNumber, locale } = useIntl();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const cbInstance = useChargebee();
  const token = useSelector(getToken);
  const profile = useSelector(getProfile);
  const [subscription, setSubscription] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [paymentSource, setPaymentSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const plans = useSelector(getPlans);

  useEffect(() => {
    if (loading || success || error) return;
    setLoading(true);
    setSubscription(null);
    setCustomer(null);
    setPaymentSource(null);
    setError(null);
    callApi(dispatch, 'users/subscriptions', token, 'GET')
      .then(ret => {
        setSuccess(true);
        setSubscription(ret.subscription);
        setCustomer(ret.customer);
        setPaymentSource(ret.paymentSource);
      })
      .catch(err => {
        if (err.status !== 404) {
          setError(err);
        } else {
          setSuccess(true);
        }
        setSubscription(null);
        setCustomer(null);
        setPaymentSource(null);
      })
      .finally(() => setLoading(false));
  }, [dispatch, token, loading, success, error]);

  const openPortalSection = useCallback(
    sectionType => async () => {
      if (cbInstance && token && !profile.trialEndsAt) {
        cbInstance.setPortalSession(() =>
          callApi(dispatch, 'users/subscriptions/portal-session', token, 'POST'),
        );
        const chargebeePortalInstance = cbInstance.createChargebeePortal();
        chargebeePortalInstance.openSection(
          {
            sectionType: sectionType,
            params: subscription ? { subscriptionId: subscription.id } : {},
          },
          {
            scheduledCancellationRemoved(data) {
              callApi(dispatch, 'users/subscriptions', token, 'PUT', data).then(() => {
                setSuccess(false);
              });
            },
            subscriptionCancelled(data) {
              callApi(dispatch, 'users/subscriptions', token, 'DELETE', data).then(() => {
                setSuccess(false);
              });
            },
            subscriptionChanged(data) {
              callApi(dispatch, 'users/subscriptions', token, 'PUT', data).then(() => {
                setSuccess(false);
              });
            },
            subscriptionReactivated(data) {
              callApi(dispatch, 'users/subscriptions', token, 'PUT', data).then(() => {
                setSuccess(false);
              });
            },
            paymentSourceAdded() {
              setSuccess(false);
            },
            paymentSourceUpdate() {
              setSuccess(false);
            },
            paymentSourceRemove() {
              setSuccess(false);
            },
          },
        );
      }
    },
    [cbInstance, token, profile.trialEndsAt, dispatch, subscription],
  );

  // Show trial message if user is still on trial
  if (profile.trialEndsAt) {
    return (
      <Paper style={{ padding: '2em', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Helmet title={formatMessage(messages.title)} />
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {formatMessage(messages.title)}
          </Typography>
          <Typography variant="body1">
            <FormattedMessage
              id="Billing.TrialActive"
              defaultMessage="Your trial is currently active. Billing information will be available once you upgrade to a paid plan."
            />
          </Typography>
        </Box>
        <Box sx={{ mt: 10 }}>
          <PricingPlans />
        </Box>
      </Paper>
    );
  }

  const getPlanName = planId => {
    const plan = plans.find(p => p.id === planId);
    return plan?.name || 'Survey ' + planId;
  };

  const getEmailLimit = planId => {
    const plan = plans.find(p => p.id === planId);
    return plan?.meta_data.people || 'unlimited';
  };

  const formatDate = timestamp => {
    return new Date(timestamp * 1000).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = status => {
    const colors = {
      active: 'success',
      trial: 'info',
      cancelled: 'error',
      past_due: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = status => {
    const labels = {
      active: formatMessage(messages.statusActive),
      trial: formatMessage(messages.statusTrial),
      cancelled: formatMessage(messages.statusCancelled),
      past_due: formatMessage(messages.statusPastDue),
    };
    return labels[status] || status;
  };

  return (
    <Paper style={{ padding: '2em' }}>
      <Helmet title={formatMessage(messages.title)} />
      <Typography variant="h5" component="h2" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>

      {error && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'error.main' }}>
          <CardContent>
            <Typography variant="body1" color="error">
              {formatMessage(messages.errorLoading)}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Subscription Details */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={120} height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={180} height={48} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={250} height={20} />
                </Box>
                <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </CardContent>
          </Card>
        ) : subscription ? (
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" component="h3">
                      {getPlanName(subscription.plan_id)}
                    </Typography>
                    <Chip
                      label={getStatusLabel(subscription.status)}
                      size="small"
                      sx={{ color: 'white' }}
                      color={getStatusColor(subscription.status)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                    <Typography variant="h4" component="span" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(subscription.plan_amount / 100, {
                        style: 'currency',
                        currency: subscription.currency_code,
                      })}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {subscription.billing_period_unit === 'month' ? (
                        <FormattedMessage
                          id="PricingPlan.Month"
                          defaultMessage="/ {period, plural, one {month} other {# months}}"
                          values={{
                            period: subscription.billing_period,
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="PricingPlan.Year"
                          defaultMessage="/ {period, plural, one {year} other {# years}}"
                          values={{
                            period: subscription.billing_period,
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <FormattedMessage
                      id="PricingPlan.Sentence"
                      defaultMessage="Send NPS surveys to {number} people monthly."
                      values={{ number: formatNumber(getEmailLimit(subscription.plan_id)) }}
                    />
                  </Typography>
                  {subscription.next_billing_at && (
                    <Typography variant="body2" color="text.secondary">
                      <FormattedMessage
                        {...messages.nextBilling}
                        values={{ date: formatDate(subscription.next_billing_at) }}
                      />
                    </Typography>
                  )}
                </Box>
                <Button variant="outlined" size="small" onClick={openPortalSection('sub_details')}>
                  {formatMessage(messages.manage)}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ mt: 10 }}>
            <PricingPlans />
          </Box>
        )}

        {/* Billing Address */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.billingAddress)}
              </Typography>
              <Skeleton variant="text" width={200} height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={180} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={150} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width={140} height={20} />
            </CardContent>
          </Card>
        ) : customer?.billing_address ? (
          <Card
            variant="outlined"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.billingAddress)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {customer.billing_address.first_name} {customer.billing_address.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {customer.billing_address.line1}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {customer.billing_address.city}, {customer.billing_address.state}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {customer.billing_address.country}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="text"
                size="small"
                sx={{ m: 1 }}
                onClick={openPortalSection('portal_address')}
              >
                {formatMessage(messages.editBillingAddress)}
              </Button>
            </CardActions>
          </Card>
        ) : null}

        {/* Payment Methods */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.paymentMethods)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="rectangular" width={50} height={30} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" width={150} height={24} />
              </Box>
              <Skeleton variant="text" width={180} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width={140} height={20} />
            </CardContent>
          </Card>
        ) : paymentSource?.card ? (
          <Card
            variant="outlined"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.paymentMethods)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 30,
                    backgroundColor: '#1a365d',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}
                  >
                    {paymentSource.card.brand.toUpperCase()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    •••• •••• •••• {paymentSource.card.last4}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      {...messages.cardExpires}
                      values={{
                        month: paymentSource.card.expiry_month,
                        year: paymentSource.card.expiry_year,
                      }}
                    />
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {paymentSource.card.first_name} {paymentSource.card.last_name}
                <br />
                {paymentSource.card.billing_country}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="text"
                size="small"
                sx={{ m: 1 }}
                onClick={openPortalSection('portal_payment_methods')}
              >
                {formatMessage(messages.editPaymentMethod)}
              </Button>
            </CardActions>
          </Card>
        ) : null}

        {/* Billing History */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={300} height={20} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" width={180} height={36} sx={{ borderRadius: 1 }} />
            </CardContent>
          </Card>
        ) : (
          !subscription || (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  {formatMessage(messages.billingHistory)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {formatMessage(messages.billingHistoryDescription)}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={openPortalSection('portal_billing_history')}
                >
                  {formatMessage(messages.viewBillingHistory)}
                </Button>
              </CardContent>
            </Card>
          )
        )}
      </Box>
    </Paper>
  );
};

export default BillingPage;
